/**
 * API Response Normalizer
 * 
 * This module provides utilities to normalize API responses across the application.
 * It handles different response formats and ensures consistent data structures.
 * 
 * USAGE:
 * import { normalizeList, normalizeItem, normalizeError } from '../lib/apiNormalizer';
 * 
 * // In your component:
 * const data = normalizeList(response.data);
 * const item = normalizeItem(response.data);
 * const errorMsg = normalizeError(error);
 */

/**
 * Normalize a list response from various API formats
 * Handles: arrays, {data: []}, {items: []}, {[key]: []}, paginated responses
 * 
 * @param {any} response - The API response (can be axios response or just data)
 * @param {string} listKey - Optional key to extract list from (e.g., 'leads', 'tickets')
 * @returns {Array} - Always returns an array, empty if no valid data found
 */
export function normalizeList(response, listKey = null) {
  // Handle null/undefined
  if (response == null) return [];
  
  // If it's an axios response, extract data
  const data = response?.data !== undefined ? response.data : response;
  
  // Direct array
  if (Array.isArray(data)) return data;
  
  // If listKey is specified, try to extract from that key
  if (listKey && data?.[listKey] !== undefined) {
    return Array.isArray(data[listKey]) ? data[listKey] : [];
  }
  
  // Common list wrapper keys (order matters - most specific first)
  const listKeys = ['items', 'data', 'results', 'records', 'list', 
                    // Domain-specific keys
                    'leads', 'tickets', 'jobs', 'quotes', 'invoices', 
                    'clients', 'users', 'employees', 'activities',
                    'statuses', 'sources', 'stages', 'notes', 'tasks',
                    'payments', 'orders', 'products', 'vendors'];
  
  for (const key of listKeys) {
    if (data?.[key] !== undefined && Array.isArray(data[key])) {
      return data[key];
    }
  }
  
  // If data is an object but not an array, check if it has any array property
  if (typeof data === 'object' && data !== null) {
    const arrayProps = Object.entries(data).filter(([_, v]) => Array.isArray(v));
    if (arrayProps.length === 1) {
      return arrayProps[0][1];
    }
  }
  
  // Return empty array as fallback
  return [];
}

/**
 * Normalize a single item response
 * 
 * @param {any} response - The API response
 * @param {string} itemKey - Optional key to extract item from (e.g., 'lead', 'ticket')
 * @returns {Object|null} - The normalized item or null
 */
export function normalizeItem(response, itemKey = null) {
  // Handle null/undefined
  if (response == null) return null;
  
  // If it's an axios response, extract data
  const data = response?.data !== undefined ? response.data : response;
  
  // If itemKey is specified, try to extract from that key
  if (itemKey && data?.[itemKey] !== undefined) {
    return data[itemKey];
  }
  
  // Common item wrapper keys
  const itemKeys = ['item', 'data', 'result', 'record',
                    // Domain-specific keys
                    'lead', 'ticket', 'job', 'quote', 'invoice',
                    'client', 'user', 'employee'];
  
  for (const key of itemKeys) {
    if (data?.[key] !== undefined && typeof data[key] === 'object' && !Array.isArray(data[key])) {
      return data[key];
    }
  }
  
  // If data is already an object (not array), return it
  if (typeof data === 'object' && !Array.isArray(data)) {
    return data;
  }
  
  return null;
}

/**
 * Normalize error response to a user-friendly message
 * Handles Pydantic validation errors, FastAPI errors, and generic errors
 * 
 * @param {any} error - The error object (can be axios error or generic error)
 * @param {string} defaultMessage - Default message if no specific error found
 * @returns {string} - Human-readable error message
 */
export function normalizeError(error, defaultMessage = 'An error occurred') {
  // Handle null/undefined
  if (!error) return defaultMessage;
  
  // Handle string errors
  if (typeof error === 'string') return error;
  
  // Extract detail from axios error response
  const detail = error?.response?.data?.detail || error?.detail;
  
  // If no detail, check for message
  if (!detail) {
    return error?.message || error?.response?.statusText || defaultMessage;
  }
  
  // If detail is a string, return it
  if (typeof detail === 'string') return detail;
  
  // Handle Pydantic validation errors (array of {type, loc, msg, input, ctx, url})
  if (Array.isArray(detail)) {
    const messages = detail.map(err => {
      if (typeof err === 'string') return err;
      if (err?.msg) {
        // Include field location for better context
        const field = err?.loc?.slice(-1)?.[0];
        return field ? `${field}: ${err.msg}` : err.msg;
      }
      if (err?.message) return err.message;
      return JSON.stringify(err);
    });
    return messages.join(', ');
  }
  
  // Handle object with msg property
  if (typeof detail === 'object' && detail?.msg) {
    return detail.msg;
  }
  
  // Handle object with message property (e.g., {error: "...", message: "..."})
  if (typeof detail === 'object' && detail?.message) {
    return detail.message;
  }
  
  // Handle any other object - stringify it
  if (typeof detail === 'object') {
    return detail?.error || JSON.stringify(detail);
  }
  
  return defaultMessage;
}

/**
 * Normalize pagination metadata from response
 * 
 * @param {any} response - The API response
 * @returns {Object} - Normalized pagination object
 */
export function normalizePagination(response) {
  const data = response?.data !== undefined ? response.data : response;
  
  return {
    total: data?.total ?? data?.count ?? data?.total_count ?? 0,
    page: data?.page ?? data?.current_page ?? 1,
    pageSize: data?.page_size ?? data?.per_page ?? data?.limit ?? 10,
    totalPages: data?.total_pages ?? data?.pages ?? Math.ceil((data?.total ?? 0) / (data?.page_size ?? 10)),
    hasNext: data?.has_next ?? data?.hasNext ?? false,
    hasPrev: data?.has_prev ?? data?.hasPrev ?? false,
  };
}

/**
 * Normalize count response
 * 
 * @param {any} response - The API response
 * @returns {number} - The count value
 */
export function normalizeCount(response) {
  const data = response?.data !== undefined ? response.data : response;
  
  if (typeof data === 'number') return data;
  if (typeof data?.count === 'number') return data.count;
  if (typeof data?.total === 'number') return data.total;
  if (typeof data?.total_count === 'number') return data.total_count;
  
  return 0;
}

/**
 * Safe access to nested properties with fallback
 * 
 * @param {Object} obj - The object to access
 * @param {string} path - Dot-notation path (e.g., 'user.profile.name')
 * @param {any} fallback - Fallback value if path doesn't exist
 * @returns {any} - The value at path or fallback
 */
export function safeGet(obj, path, fallback = undefined) {
  if (!obj || !path) return fallback;
  
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result == null || typeof result !== 'object') {
      return fallback;
    }
    result = result[key];
  }
  
  return result !== undefined ? result : fallback;
}

/**
 * Create a normalized API response wrapper
 * Use this to wrap API calls for consistent response handling
 * 
 * @param {Promise} apiCall - The API promise
 * @param {Object} options - Options for normalization
 * @returns {Promise} - Normalized response
 */
export async function normalizeApiCall(apiCall, options = {}) {
  const { 
    listKey = null, 
    itemKey = null, 
    isList = false,
    defaultValue = null 
  } = options;
  
  try {
    const response = await apiCall;
    
    if (isList) {
      return {
        success: true,
        data: normalizeList(response, listKey),
        pagination: normalizePagination(response),
        raw: response?.data
      };
    }
    
    return {
      success: true,
      data: itemKey ? normalizeItem(response, itemKey) : (response?.data ?? response),
      raw: response?.data
    };
  } catch (error) {
    return {
      success: false,
      data: defaultValue ?? (isList ? [] : null),
      error: normalizeError(error),
      raw: error?.response?.data
    };
  }
}

export default {
  normalizeList,
  normalizeItem,
  normalizeError,
  normalizePagination,
  normalizeCount,
  safeGet,
  normalizeApiCall
};
